import { Search, X } from 'lucide-react';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../cn';

const fieldBase =
  // Set into the housing rather than raised from it: a faint inner shadow at
  // the top reads as a recess, the opposite of a key.
  'w-full rounded-md border border-line-strong bg-surface text-ink shadow-[inset_0_1px_2px_rgb(27_27_24/0.05)] transition-[border-color,box-shadow] duration-150 ' +
  'hover:border-ink-4 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/12 ' +
  'disabled:bg-sunken disabled:text-ink-3 aria-invalid:border-danger aria-invalid:focus:ring-danger/15';

const inputSizes = {
  sm: 'h-8 px-2.5 text-sm',
  md: 'h-10 px-3 text-base',
  lg: 'h-12 px-3.5 text-md',
};

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: keyof typeof inputSizes;
  /** Content before the text (icon, currency). */
  leading?: ReactNode;
  /** Content after the text (unit, action). */
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', leading, trailing, className, ...rest },
  ref,
) {
  if (!leading && !trailing) {
    return <input ref={ref} className={cn(fieldBase, inputSizes[size], className)} {...rest} />;
  }
  return (
    <div className={cn('relative flex items-center', className)}>
      {leading && (
        <span className="pointer-events-none absolute left-3 flex items-center text-ink-3">
          {leading}
        </span>
      )}
      <input
        ref={ref}
        className={cn(fieldBase, inputSizes[size], leading && 'pl-10', trailing && 'pr-12')}
        {...rest}
      />
      {trailing && (
        <span className="absolute right-2 flex items-center text-ink-3">{trailing}</span>
      )}
    </div>
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(fieldBase, 'min-h-20 px-3 py-2 text-base', className)}
      {...rest}
    />
  );
});

export interface SearchFieldProps extends Omit<InputProps, 'leading' | 'type'> {
  shortcut?: string;
  onClear?: () => void;
}

/** Large search input with a shortcut hint. Used for the POS product search. */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { shortcut, onClear, value, size = 'lg', className, ...rest },
  ref,
) {
  const hasValue = typeof value === 'string' ? value.length > 0 : Boolean(value);
  return (
    <Input
      ref={ref}
      type="search"
      size={size}
      value={value}
      leading={<Search className="size-5" strokeWidth={1.75} />}
      trailing={
        hasValue && onClear ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Очистить поиск"
            className="rounded-sm p-1.5 text-ink-3 hover:bg-rail hover:text-ink"
          >
            <X className="size-4" />
          </button>
        ) : shortcut ? (
          <Kbd>{shortcut}</Kbd>
        ) : null
      }
      className={cn('[&_input]:[&::-webkit-search-cancel-button]:hidden', className)}
      autoComplete="off"
      spellCheck={false}
      {...rest}
    />
  );
});

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        // A shortcut hint is drawn as the key it names.
        'key inline-flex h-6 min-w-6 items-center justify-center rounded-xs px-1.5 font-sans text-2xs font-semibold text-ink-3 tnum',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  /** Render prop receives the id and aria props to spread on the control. */
  children: (props: {
    id: string;
    'aria-invalid'?: true;
    'aria-describedby'?: string;
  }) => ReactNode;
}

/** Label + control + hint/error, wired for accessibility. */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink-2">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {error ? (
        <p id={`${id}-error`} className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
